import { FormControlLabel, IconButton, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  DeleteIcon,
  GKButton,
  GKCheckbox,
  GKSearchSelect,
  GKTextField,
  Grid2,
  PlusIcon,
} from "components";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import {
  FeeMethod,
  FeeType,
  PERFORMANCE_PLAN_FEE_PARAMETER,
} from "utils/constants";

const PerformanceFee = () => {
  const form: any = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "performanceFee.performanceFeeSlab",
  });

  form.watch(["performanceFee.feeType"]);

  const isFeeTypeFix = form.getValues("performanceFee.feeType") !== "fixed";

  return (
    <Grid2 container rowSpacing={2} spacing={1}>
      <Grid2 size={12}>
        <Typography fontSize={18} fontWeight={500}>
          Performance Fee
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="performanceFee.minesManagementFee"
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              control={<GKCheckbox {...field} checked={field.value} />}
              label={"Management Fee Deduction"}
            />
          )}
        />
        <Controller
          name="performanceFee.highwatermark"
          control={form.control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={<GKCheckbox {...field} checked={field.value} />}
              label={"High Water Mark"}
            />
          )}
        />
        <Controller
          name="performanceFee.hardHurdle"
          control={form.control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={<GKCheckbox {...field} checked={field.value} />}
              label={"Hard Hurdle"}
            />
          )}
        />
        <Controller
          name="performanceFee.includeDividend"
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              control={<GKCheckbox {...field} checked={field.value} />}
              label={"Include Dividend"}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          name="performanceFee.productName"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              placeholder="Product Name"
              tooltipTitle="This name will appear on the invoices generated for your clients."
              inputLabel={"Product Name"}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          name="performanceFee.hsnCode"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter HSN code",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              placeholder="HSN Code"
              requiredField
              inputLabel={"HSN Code"}
              tooltipTitle="This code will appear on invoices to classify your product for tax compliance."
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid2>
      <Grid2 size={4}>
        <Controller
          name="performanceFee.feeType"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please select a Fee Type",
            },
          }}
          render={({ field }) => (
            <GKSearchSelect
              {...field}
              requiredField
              inputLabel="Fee Type"
              disableClearable={false}
              options={
                FeeType.filter((dataItem) => dataItem.value !== "fixed") || []
              }
              onChange={(e, val) => {
                field.onChange(val?.value);
              }}
              value={
                FeeType.find((dataItem) => dataItem.value === field.value) || ""
              }
              getOptionLabel={(option) => option.name || option}
              error={Boolean(form?.formState?.errors?.performanceFee?.feeType)}
              helperText={
                form?.formState?.errors?.performanceFee?.feeType?.message
              }
            />
          )}
        />
      </Grid2>
      {isFeeTypeFix && (
        <>
          <Grid2 size={4}>
            <Controller
              name="performanceFee.feeMethod"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Fee Method"
                  disableClearable={false}
                  options={FeeMethod || []}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    FeeMethod.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="performanceFee.calculationBenchmark"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Fee Parameter"
                  disableClearable={false}
                  options={PERFORMANCE_PLAN_FEE_PARAMETER}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    PERFORMANCE_PLAN_FEE_PARAMETER.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                />
              )}
            />
          </Grid2>
        </>
      )}

      <Grid2 size={4}>
        <Controller
          name="performanceFee.fee"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter a Fee",
            },
          }}
          render={({ field }) => (
            <GKTextField
              {...field}
              requiredField
              inputLabel={!isFeeTypeFix ? "Fee Amount" : "Fee %"}
              type="number"
              error={Boolean(form?.formState?.errors?.performanceFee?.fee)}
              helperText={form?.formState?.errors?.performanceFee?.fee?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={4}>
        <Controller
          name="performanceFee.outOfPocketExpenses"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              inputLabel="Out Of Pocket Expense"
              type="number"
            />
          )}
        />
      </Grid2>
      {/* <Grid2 size={4}>

          <Controller
            name="performanceFee.minimumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Minimum Fee"
                type="number"
              />
            )}
          />

      </Grid2>
      <Grid2 size={4}>

          <Controller
            name="performanceFee.maximumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Maximum Fee"
                type="number"
              />
            )}
          />

      </Grid2> */}
      {/* <Grid2 size={4}>

          <Controller
            name="performanceFee.softHurdle"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel="Soft Hurdle"
                type="number"
              />
            )}
          />

      </Grid2> */}
      {form.watch("performanceFee.hardHurdle") && (
        <Grid2 size={4}>
          <Controller
            name="performanceFee.hurdle"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Hurdle %" type="number" />
            )}
          />
        </Grid2>
      )}

      {form.getValues("performanceFee.feeMethod") === "slabs" && (
        <Grid2 size={12}>
          <TableContainer
            component={Paper}
            sx={{ marginTop: 2, minHeight: 100 }}
          >
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    style={{
                      padding: 12,
                    }}
                  >
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color={"var(--primary-900)"}
                    >
                      Slabs
                    </Typography>
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    align="right"
                    style={{
                      padding: 12,
                    }}
                  >
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color={"var(--primary-900)"}
                    >
                      <GKButton
                        variant="outlined"
                        onClick={() => {
                          append({
                            lowerLimit: "",
                            upperLimit: "",
                            slabFee: "",
                          });
                        }}
                        startIcon={<PlusIcon />}
                      >
                        Add Slab
                      </GKButton>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Management Fee&nbsp;(%)</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((row: any, index: number) => {
                  return (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Controller
                          name={`performanceFee.performanceFeeSlab[${index}].lowerLimit`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`performanceFee.performanceFeeSlab[${index}].upperLimit`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`performanceFee.performanceFeeSlab[${index}].slabFee`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} type="number" />
                          )}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          className="outline"
                          disabled={fields.length === 1}
                          onClick={() => remove(index)}
                        >
                          <DeleteIcon width={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>
      )}
    </Grid2>
  );
};

export default PerformanceFee;
